import { DEFAULT_IDB_DB_NAME, DEFAULT_IDB_STORE_NAME, IDB_DB_VERSION } from './constants';
import type { S3PartRecord } from '@clarix/shared-types';

export interface RecordingDbConfig {
  dbName?: string;
  storeName?: string;
  dbVersion?: number;
}

/**
 * IndexedDB wrapper for persisting S3 upload parts during recording.
 *
 * Each record represents one S3 multipart part (a merged blob ready to upload).
 * Parts survive tab crashes, page refreshes, and browser restarts, enabling
 * reliable resumption. Apps may supply a custom `dbName` to keep different apps'
 * recordings isolated in separate IndexedDB databases.
 *
 * Lifecycle:
 *   savePart()     → status: 'queued'
 *   markUploading() → status: 'uploading'
 *   markDone()     → status: 'done',  etag set
 *   markFailed()   → status: 'failed' (eligible for retry)
 *   clearRecording() → deletes all records for a recording
 */
export class RecordingDB {
  private db: IDBDatabase | null = null;
  private readonly dbName: string;
  private readonly storeName: string;
  private readonly dbVersion: number;

  constructor(config?: RecordingDbConfig) {
    this.dbName = config?.dbName ?? DEFAULT_IDB_DB_NAME;
    this.storeName = config?.storeName ?? DEFAULT_IDB_STORE_NAME;
    this.dbVersion = config?.dbVersion ?? IDB_DB_VERSION;
  }

  /** Open (or create) the IndexedDB database. Call once before using other methods. */
  open(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      const storeName = this.storeName;

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, {
            keyPath: 'id',
            autoIncrement: true,
          });
          // Index by recording_id for fast lookups
          store.createIndex('by_recording', 'recording_id', { unique: false });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Persist a new S3 part blob with status 'queued'.
   * Returns the auto-generated record id.
   */
  savePart(recordingId: string, s3PartNumber: number, blob: Blob): Promise<number> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const record: Omit<S3PartRecord, 'id'> = {
        recording_id: recordingId,
        s3_part_number: s3PartNumber,
        blob,
        status: 'queued',
        etag: null,
        created_at: Date.now(),
      };
      const req = store.add(record);
      req.onsuccess = () => resolve(req.result as number);
      req.onerror = () => reject(req.error);
    });
  }

  markUploading(id: number): Promise<void> {
    return this._updateField(id, { status: 'uploading' });
  }

  markDone(id: number, etag: string): Promise<void> {
    return this._updateField(id, { status: 'done', etag });
  }

  markFailed(id: number): Promise<void> {
    return this._updateField(id, { status: 'failed' });
  }

  /**
   * Returns all parts for `recordingId` whose status is queued, uploading, or failed.
   * Ordered by s3_part_number ascending.
   */
  getPendingParts(recordingId: string): Promise<S3PartRecord[]> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('by_recording');
      const req = index.getAll(recordingId);
      req.onsuccess = () => {
        const all = req.result as S3PartRecord[];
        resolve(
          all
            .filter(
              (r) => r.status === 'queued' || r.status === 'uploading' || r.status === 'failed',
            )
            .sort((a, b) => a.s3_part_number - b.s3_part_number),
        );
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Returns all successfully uploaded parts sorted by s3_part_number.
   * Used to build the `parts` payload for completeUpload.
   */
  getCompletedParts(recordingId: string): Promise<{ part_number: number; etag: string }[]> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('by_recording');
      const req = index.getAll(recordingId);
      req.onsuccess = () => {
        const all = req.result as S3PartRecord[];

        // Deduplicate by etag: multiple IDB records merged into one S3 part
        // share the same etag. Only keep one entry per unique etag, using the
        // lowest s3_part_number as the canonical part number.
        const seen = new Map<string, { part_number: number; etag: string }>();
        for (const r of all) {
          if (r.status !== 'done' || !r.etag) continue;
          const existing = seen.get(r.etag);
          if (!existing || r.s3_part_number < existing.part_number) {
            seen.set(r.etag, { part_number: r.s3_part_number, etag: r.etag });
          }
        }

        resolve(Array.from(seen.values()).sort((a, b) => a.part_number - b.part_number));
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Sends uploaded parts again, for when the server reports it lacks them: the records of each named
   * S3 part (all parts when `partNumbers` is empty) go back to `queued`. Returns how many moved.
   */
  async requeueParts(recordingId: string, partNumbers: readonly number[]): Promise<number> {
    const again = selectRequeue(await this.getParts(recordingId), partNumbers);
    for (const r of again) {
      if (r.id !== undefined) await this._updateField(r.id, { status: 'queued', etag: null });
    }
    return again.length;
  }

  /**
   * After the server re-opened a lost upload: every record with audio goes back to `queued` under
   * its part number, and the zero-byte markers, which point into the lost upload, are dropped.
   */
  async restartUpload(recordingId: string): Promise<number> {
    let queued = 0;
    for (const r of await this.getParts(recordingId)) {
      if (r.id === undefined) continue;
      if (r.blob.size === 0) {
        await this._delete(r.id);
      } else {
        await this._updateField(r.id, { status: 'queued', etag: null });
        queued++;
      }
    }
    return queued;
  }

  /** Every record of a recording, in part order: the audio still on this device. */
  getParts(recordingId: string): Promise<S3PartRecord[]> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const req = db.transaction(this.storeName, 'readonly').objectStore(this.storeName).index('by_recording').getAll(recordingId);
      req.onsuccess = () =>
        resolve(
          (req.result as S3PartRecord[]).sort((a, b) => a.s3_part_number - b.s3_part_number || (a.id ?? 0) - (b.id ?? 0)),
        );
      req.onerror = () => reject(req.error);
    });
  }

  /** Delete all records belonging to `recordingId`. */
  clearRecording(recordingId: string): Promise<void> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('by_recording');
      const keysReq = index.getAllKeys(recordingId);
      keysReq.onsuccess = () => {
        const keys = keysReq.result as number[];
        if (keys.length === 0) {
          resolve();
          return;
        }
        let remaining = keys.length;
        const onDone = () => {
          remaining--;
          if (remaining === 0) resolve();
        };
        for (const key of keys) {
          const delReq = store.delete(key);
          delReq.onsuccess = onDone;
          delReq.onerror = () => reject(delReq.error);
        }
      };
      keysReq.onerror = () => reject(keysReq.error);
    });
  }

  close(): void {
    this.db?.close();
    this.db = null;
  }

  // ── Private helpers ─────────────────────────────────────────────────

  private _delete(id: number): Promise<void> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const req = db.transaction(this.storeName, 'readwrite').objectStore(this.storeName).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private _updateField(id: number, fields: Partial<S3PartRecord>): Promise<void> {
    const db = this.db!;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const record = { ...(getReq.result as S3PartRecord), ...fields };
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }
}

/**
 * The uploaded records to send again. Zero-byte records only mark parts the fallback recorder
 * uploaded from memory: they carry no audio, so they are never re-queued, and a named part whose
 * records are all empty is left for the server's ListParts to decide.
 */
export function selectRequeue(records: readonly S3PartRecord[], partNumbers: readonly number[]): S3PartRecord[] {
  const done = records.filter((r) => r.status === 'done' && r.etag);
  const partOf = new Map<string, number>();
  for (const r of done) partOf.set(r.etag!, Math.min(partOf.get(r.etag!) ?? Infinity, r.s3_part_number));
  const wanted = new Set(partNumbers);
  return done.filter((r) => r.blob.size > 0 && (wanted.size === 0 || wanted.has(partOf.get(r.etag!)!)));
}
